import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'

export interface UseHistoricalActionOptions {
  /**
   * Optional function to match metadata. Returns true if the action's metadata matches.
   * @param metadata - The metadata object from the historical action
   * @returns true if the metadata matches the criteria
   */
  metadataMatcher?: (metadata?: Record<string, any>) => boolean
  /**
   * Minimum number of occurrences required for the action to be considered as having occurred.
   * Defaults to 1. Ignored if `onlyLatest` is true.
   */
  minCount?: number
  /**
   * If true, only the most recent action matching the type(s) will be checked.
   * When enabled, `minCount` is ignored and only the latest matching action is evaluated.
   * Defaults to false.
   */
  onlyLatest?: boolean
}

export interface UseHistoricalActionResult {
  /**
   * Whether the action has occurred (based on the provided criteria)
   */
  hasOccurred: boolean
  /**
   * Number of matching actions found
   */
  count: number
  /**
   * The most recent matching action, if any
   */
  lastOccurrence: App.HistoricalAction | undefined
  /**
   * All matching actions, sorted by timestamp (most recent first)
   */
  allMatches: Array<App.HistoricalAction>
}

/**
 * Helper function to compare two historical actions by timestamp in descending order
 * (most recent first). Used as a comparator for Array.sort().
 */
export function compareHistoricalActionsByTimestamp(
  a: App.HistoricalAction,
  b: App.HistoricalAction,
): number {
  const timeA = new Date(a.timestamp).getTime()
  const timeB = new Date(b.timestamp).getTime()
  return timeB - timeA
}

/**
 * Hook for reactive feature enablement based on historical user actions.
 *
 * This hook checks if a specific action (or actions) has occurred in the user's session history
 * and reactively updates when new actions are added. Useful for enabling features based on
 * user behavior patterns.
 *
 * @param actionType - The type(s) of action to check for. Can be a single string or an array of strings.
 * @param options - Optional configuration for matching criteria
 * @returns An object containing `hasOccurred`, `count`, `lastOccurrence`, and `allMatches`
 *
 * @example
 * ```tsx
 * // Simple check for a single action type
 * const { hasOccurred } = useHistoricalAction('button_click')
 * if (hasOccurred) {
 *   // Enable feature
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Check for multiple action types
 * const { hasOccurred } = useHistoricalAction(['page_view', 'button_click'])
 * if (hasOccurred) {
 *   // Enable feature if either action occurred
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Check with metadata matching
 * const { hasOccurred } = useHistoricalAction('form_submit', {
 *   metadataMatcher: (metadata) => metadata?.formId === 'checkout'
 * })
 * ```
 *
 * @example
 * ```tsx
 * // Check with minimum count requirement
 * const { hasOccurred, count } = useHistoricalAction('offer_viewed', {
 *   minCount: 3
 * })
 * // Feature enabled only after viewing 3+ offers
 * ```
 *
 * @example
 * ```tsx
 * // Only check the latest action (ignore older occurrences)
 * const { hasOccurred, lastOccurrence } = useHistoricalAction('form_submit', {
 *   onlyLatest: true,
 *   metadataMatcher: (metadata) => metadata?.formId === 'checkout'
 * })
 * ```
 */
export function useHistoricalAction(
  actionType: string | Array<string>,
  options: UseHistoricalActionOptions = {},
): UseHistoricalActionResult {
  const { metadataMatcher, minCount = 1, onlyLatest = false } = options

  const historicalActions = useAppSelector(state => state.system.features.historicalActions)

  const result = useMemo(() => {
    const actionTypes = Array.isArray(actionType) ? actionType : [actionType]

    if (onlyLatest) {
      // Find all actions matching the type (for finding the latest)
      const typeMatches = historicalActions.filter(action =>
        actionTypes.includes(action.type),
      )

      // Sort by timestamp descending (most recent first)
      const sortedTypeMatches = [...typeMatches].sort(compareHistoricalActionsByTimestamp)
      const latestActionByType = sortedTypeMatches[0]

      // Check if the latest action matches metadata (if provided)
      const latestActionMatches =
        latestActionByType !== undefined &&
        (metadataMatcher === undefined || metadataMatcher(latestActionByType.metadata))

      // Find all matching actions (type + metadata) for count and allMatches
      const matches = historicalActions.filter(action => {
        // Check if action type matches
        if (!actionTypes.includes(action.type)) {
          return false
        }

        // Check metadata matcher if provided
        if (metadataMatcher && !metadataMatcher(action.metadata)) {
          return false
        }

        return true
      })

      // Sort by timestamp descending (most recent first)
      const sortedMatches = [...matches].sort(compareHistoricalActionsByTimestamp)

      const count = sortedMatches.length
      const latestAction = sortedMatches[0]

      // When onlyLatest is true, hasOccurred is based only on the latest action matching
      const hasOccurred = latestActionMatches

      return {
        hasOccurred,
        count,
        lastOccurrence: latestAction,
        allMatches: sortedMatches,
      }
    }

    // Original logic for matching all actions
    const matches = historicalActions.filter(action => {
      // Check if action type matches
      if (!actionTypes.includes(action.type)) {
        return false
      }

      // Check metadata matcher if provided
      if (metadataMatcher && !metadataMatcher(action.metadata)) {
        return false
      }

      return true
    })

    // Sort by timestamp descending (most recent first)
    const sortedMatches = [...matches].sort(compareHistoricalActionsByTimestamp)

    const count = sortedMatches.length
    const hasOccurred = count >= minCount
    const lastOccurrence = sortedMatches[0]

    return {
      hasOccurred,
      count,
      lastOccurrence,
      allMatches: sortedMatches,
    }
  }, [historicalActions, actionType, metadataMatcher, minCount, onlyLatest])

  return result
}
